/**
 * Lightweight in-memory cache for GET requests.
 * Returns stale data instantly while revalidating in the background.
 *
 * Usage:
 *   import { cachedGet, prefetch, invalidate } from './apiCache';
 *   const data = await cachedGet('/companies?limit=500');
 *   prefetch(['/companies?limit=500']);      // warm cache early
 *   invalidate('/companies');                // bust after mutation
 */
import api from './api';

const cache = new Map();  // key → { data, ts, promise }

const DEFAULT_TTL = 2 * 60 * 1000;   // 2 minutes — fresh window
const STALE_TTL  = 10 * 60 * 1000;   // 10 minutes — serve stale + revalidate

/**
 * GET with cache. Within TTL → instant. Within STALE_TTL → instant + background refresh.
 * Beyond STALE_TTL → fresh fetch (still deduped).
 */
export async function cachedGet(url, { ttl = DEFAULT_TTL, staleTtl = STALE_TTL } = {}) {
  const now = Date.now();
  const entry = cache.get(url);

  // Fresh cache hit — return immediately
  if (entry?.data && (now - entry.ts) < ttl) {
    return entry.data;
  }

  // Stale cache hit — return stale, revalidate in background
  if (entry?.data && (now - entry.ts) < staleTtl) {
    // Only kick off one background revalidation
    if (!entry.promise) {
      entry.promise = api.get(url).then(res => {
        cache.set(url, { data: res.data, ts: Date.now(), promise: null });
        return res.data;
      }).catch(() => {
        entry.promise = null; // allow retry
      });
    }
    return entry.data;
  }

  // No cache or expired — fetch (deduped: reuse in-flight promise)
  if (entry?.promise) {
    return entry.promise;
  }

  const promise = api.get(url).then(res => {
    cache.set(url, { data: res.data, ts: Date.now(), promise: null });
    return res.data;
  }).catch(err => {
    // On error, clear promise so next call retries
    const e = cache.get(url);
    if (e) e.promise = null;
    throw err;
  });

  cache.set(url, { data: entry?.data || null, ts: entry?.ts || 0, promise });
  return promise;
}

/**
 * Prefetch URLs into cache. Fire-and-forget.
 */
export function prefetch(urls) {
  urls.forEach(url => cachedGet(url).catch(() => {}));
}

/**
 * Invalidate cache entries matching a prefix.
 *   invalidate('/companies') clears '/companies', '/companies?limit=500', etc.
 */
export function invalidate(urlPrefix) {
  for (const key of cache.keys()) {
    if (key === urlPrefix || key.startsWith(urlPrefix + '?') || key.startsWith(urlPrefix + '/')) {
      cache.delete(key);
    }
  }
}

/**
 * Clear entire cache (e.g., on logout).
 */
export function clearCache() {
  cache.clear();
}
